import { CoreRow, createColumnHelper, SortingState, Updater } from '@tanstack/react-table';
import GenericTable from '@/components/GenericTable/GenericTable';
import React, { FC, useMemo } from 'react';
import { SortTransaction, Transaction } from '@/services/transactions/TransactionTypes';
import { twMerge } from 'tailwind-merge';
import { formatWalletAddress } from '@/common/utils/utils';
import config from '@/common/config';
import { LiaExternalLinkAltSolid } from 'react-icons/lia';
import { isAddress } from 'viem';
import Badge, { BADGE_STATUS } from '@/components/Badge/Badge';
import { toast } from '@/components/toast/useToast';
import { formatCurrency } from '@/pages/table/utils/utils';
import { FiCopy } from 'react-icons/fi';

const columnHelper = createColumnHelper<Transaction>();

const transformLabel = (label: string) => {
  return label
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^\w/, (char) => char.toUpperCase());
};

const TableLabels = ({ primaryLabel, secondaryLabel }: { primaryLabel: string; secondaryLabel: string }) => {
  return (
    <div className="flex flex-col">
      <span className="overflow-hidden truncate text-sm font-medium text-white md:overflow-visible md:whitespace-normal">
        {primaryLabel}
      </span>
      <span className="overflow-hidden truncate text-xs font-semibold text-white text-opacity-50 md:overflow-visible md:whitespace-normal">
        {secondaryLabel}
      </span>
    </div>
  );
};

interface TransactionsTableProps {
  data: Transaction[];
  onSortingChange: (updaterOrValue: Updater<SortingState>) => void;
  sorting: SortTransaction;
}
const TransactionsTable: FC<TransactionsTableProps> = ({ data, onSortingChange, sorting }) => {
  const handleCopyToClipboard = (value: string) => {
    toast({ description: 'Copied to clipboard', variant: 'success' });
    return navigator.clipboard.writeText(value);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('createdAt', {
        header: 'Date & Time',
        enableSorting: true,
        cell: ({ row }: { row: CoreRow<Transaction> }) => {
          const date = new Date(row.original.createdAt);
          const formattedDate = date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
          const formattedTime = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: true,
          });

          return <TableLabels primaryLabel={formattedDate} secondaryLabel={formattedTime} />;
        },
      }),
      columnHelper.accessor('type', {
        header: 'Type',
        enableSorting: true,
        cell: ({ row }: { row: CoreRow<Transaction> }) => (
          <span className="text-sm font-medium text-white">{transformLabel(row.original.type)}</span>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        enableSorting: true,
        cell: ({ row }: { row: CoreRow<Transaction> }) => {
          const getBadgeStatus = (label: string) => {
            const lowerCaseLabel = label.toLowerCase();

            if (lowerCaseLabel.includes('fail')) {
              return BADGE_STATUS.failed;
            } else if (lowerCaseLabel.includes('pending')) {
              return BADGE_STATUS.pending;
            } else {
              return BADGE_STATUS.success;
            }
          };
          return (
            <span className="text-sm font-medium text-white">
              <Badge label={transformLabel(row.original.status)} status={getBadgeStatus(row.original.status)} />
            </span>
          );
        },
      }),
      columnHelper.accessor('from', {
        header: 'From/To',
        enableSorting: true,
        cell: ({ row }: { row: CoreRow<Transaction> }) => {
          const isTransfer = row.original.amount < 0;

          const labelValue = isTransfer ? row.original.to : row.original.from;

          const label = isAddress(labelValue!) ? formatWalletAddress(labelValue) : labelValue;

          if (row.original.tableData?.name) {
            return (
              <TableLabels
                primaryLabel={row.original.tableData.name}
                secondaryLabel={`$${formatCurrency(row.original.tableData.minStake)}/$${formatCurrency(row.original.tableData.maxStake)}`}
              />
            );
          }
          return (
            <a
              title={`${config.blockExplorerUrl}/tx/${row.original.txId}`}
              href={`${config.blockExplorerUrl}/tx/${row.original.txId}`}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-white"
            >
              <span className="flex items-center gap-1">
                #{label}
                <LiaExternalLinkAltSolid size={20} className="text-ph-purple" />
              </span>
            </a>
          );
        },
      }),
      columnHelper.accessor('txId', {
        header: 'Transaction ID',
        enableSorting: true,
        cell: ({ row }: { row: CoreRow<Transaction> }) => {
          if (row.original.txId) {
            return (
              <div className="flex flex-col">
                <span className="flex items-center gap-3 text-xs font-medium text-white">
                  #{formatWalletAddress(row.original.txId)}
                  <FiCopy
                    size={14}
                    className="w-full text-ph-purple hover:cursor-pointer"
                    onClick={() => handleCopyToClipboard(row.original.txId)}
                  />
                </span>
                <a
                  title={`${config.blockExplorerUrl}/tx/${row.original.txId}`}
                  href={`${config.blockExplorerUrl}/tx/${row.original.txId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-white"
                >
                  <span className="flex items-center text-xs font-semibold text-ph-purple">View on blockchain</span>
                </a>
              </div>
            );
          }
        },
      }),
      columnHelper.accessor('amount', {
        header: 'Amount',
        enableSorting: true,
        cell: ({ row }: { row: CoreRow<Transaction> }) => {
          return (
            <div className="flex flex-col">
              <span
                className={twMerge(
                  'text-sm font-medium text-ph-green-dark',
                  row.original.amount < 0 && 'text-ph-red-secondary',
                )}
              >
                {row.original.amount < 0
                  ? `-$${Math.abs(row.original.amount).toFixed(2)}`
                  : `$${row.original.amount.toFixed(2)}`}
              </span>
              <span className="text-xs font-bold text-white text-opacity-50">
                {formatCurrency(row.original.newBalance)}
              </span>
            </div>
          );
        },
      }),
    ],
    [],
  );

  return (
    <GenericTable
      data={data}
      columns={columns}
      sorting={[{ id: sorting.field, desc: sorting.order === 'desc' }]}
      onSortingChange={onSortingChange}
      hover={true}
      minWidth={'min-w-[37.5rem]'}
      maxWidth={'max-w-[53.75em]'}
      gridColClass="grid-cols-[0.2fr_0.2fr_0.2fr_0.2fr_0.25fr_0.1fr] !h-16 cursor-pointer"
      gridColBorderClass={twMerge(
        '[&:nth-child(1)]:border-l [&:nth-child(1)]:border-b [&:nth-child(1)]:border-[rgba(255,255,255,0.15)]',
        '[&:nth-child(2)]:border-b [&:nth-child(2)]:border-[rgba(255,255,255,0.15)]',
        '[&:nth-child(3)]:border-b [&:nth-child(3)]:border-[rgba(255,255,255,0.15)]',
        '[&:nth-child(4)]:border-b [&:nth-child(4)]:border-[rgba(255,255,255,0.15)]',
        '[&:nth-child(5)]:border-b [&:nth-child(5)]:border-[rgba(255,255,255,0.15)]',
        '[&:nth-child(6)]:border-b [&:nth-child(6)]:border-r [&:nth-child(6)]:border-[rgba(255,255,255,0.15)]',
      )}
    />
  );
};

export default TransactionsTable;
